import { Component, OnInit, ElementRef, ViewChild, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Location } from '@angular/common';
import Swal from 'sweetalert2';

import { Piece } from 'app/models/piece';
import { PiecesService } from 'app/services/pieces/pieces.service';
import { ProjetsService } from 'app/services/projets/projets.service';
import { AwscontrolService, ImageUploadResponse } from 'app/services/aws/awscontrol.service';

type GalleryUploadMode = 'direct' | 'imagesousproduit';

@Component({
  selector: 'app-modifier-piece',
  templateUrl: './modifier-piece.component.html',
  styleUrls: ['./modifier-piece.component.scss']
})
export class ModifierPieceComponent implements OnInit, OnDestroy {
  @ViewChild('descriptionTextarea', { static: false }) descriptionTextarea!: ElementRef;
  @ViewChild('descriptionArabTextarea', { static: false }) descriptionArabTextarea!: ElementRef;
  @ViewChild('descriptionEnglishTextarea', { static: false }) descriptionEnglishTextarea!: ElementRef;
  @ViewChild('pieceImagesInput', { static: false }) pieceImagesInput!: ElementRef;

  piece: Piece = new Piece();
  agenciesProjet: any[] = [];
  uploadedImages: string[] = [];

  isLoading: boolean = false;
  isUploadingImages: boolean = false;
  private destroy$ = new Subject<void>();

  agencyAvailableLanguages: string[] = ['FR'];

  // ==================== MODE + PREVIEW (galerie) ====================
  galleryUploadMode: GalleryUploadMode = 'direct';
  galleryPreviewOriginalUrls: string[] = [];
  galleryPreviewProcessedUrls: string[] = [];
  isAwaitingGalleryValidation: boolean = false;
  // ================================================================

  private readonly FORMATTING_COMMANDS: { [key: string]: { tag: string } } = {
    bold: { tag: 'strong' },
    italic: { tag: 'em' },
    underline: { tag: 'u' },
    list: { tag: 'ul' }
  };

  statusOptions = [
    { value: 'DISPONIBLE', label: 'Disponible' },
    { value: 'EN COURS', label: 'En cours' },
    { value: 'VENDU', label: 'Vendu' },
    { value: 'SOLDE', label: 'Soldé' }
  ];

  constructor(
    private projetService: ProjetsService,
    private piecesService: PiecesService,
    private location: Location,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private awsService: AwscontrolService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.initializeData();
    this.subscribeToRefresh();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeData(): void {
    this.loadProjects();
    this.loadPieceData();
  }

  private subscribeToRefresh(): void {
    this.projetService.refreshNedeed
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.loadPieceData());
  }

  private loadProjects(): void {
    this.projetService.getListProjet()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: any) => {
          this.agenciesProjet = Array.isArray(data) ? data : (data ? [data] : []);
          if (this.piece.projetName && this.agencyAvailableLanguages.length === 1 && this.agencyAvailableLanguages[0] === 'FR') {
            this.setProjectLanguagesFromName(this.piece.projetName);
          }
          this.cdr.detectChanges();
        },
        error: (err) => this.handleError('Erreur lors du chargement des projets', err)
      });
  }

  private loadPieceData(): void {
    const pieceId = this.activatedRoute.snapshot.paramMap.get('id');
    if (!pieceId) {
      this.showError('ID de l\'appartement introuvable');
      return;
    }

    this.isLoading = true;
    this.piecesService.getPiece(pieceId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: any) => {
          this.piece = data;
          this.piece.nameArab = data.nameArab || '';
          this.piece.descriptionArab = data.descriptionArab || '';
          this.piece.nameEnglish = data.nameEnglish || '';
          this.piece.descriptionEnglish = data.descriptionEnglish || '';
          this.uploadedImages = data.photos ? [...data.photos] : [];

          if (data.projet && data.projet.user && data.projet.user.availableLanguages) {
            this.agencyAvailableLanguages = data.projet.user.availableLanguages;
          } else {
            this.setProjectLanguagesFromName(data.projetName);
          }

          this.isLoading = false;
          this.cdr.detectChanges();
        },
        error: (err) => {
          this.handleError('Erreur lors du chargement de l\'appartement', err);
          this.isLoading = false;
        }
      });
  }

  private setProjectLanguagesFromName(projectName: string): void {
    if (!projectName) {
      this.agencyAvailableLanguages = ['FR'];
      this.cdr.detectChanges();
      return;
    }
    const project = this.agenciesProjet.find(p => p.name === projectName);
    if (project && project.user && project.user.availableLanguages) {
      this.agencyAvailableLanguages = project.user.availableLanguages;
    } else {
      if (this.agencyAvailableLanguages.length === 0) {
        this.agencyAvailableLanguages = ['FR'];
      }
    }
    this.cdr.detectChanges();
  }

  onProjectChange(projectName: string): void {
    this.piece.projetName = projectName;
    const project = this.agenciesProjet.find(p => p.name === projectName);
    if (project && project.user && project.user.availableLanguages) {
      this.agencyAvailableLanguages = project.user.availableLanguages;
    } else {
      this.agencyAvailableLanguages = ['FR'];
    }
    this.cdr.detectChanges();
  }

  // ============= TEXT FORMATTING =============

  formatText(command: string, target: 'fr' | 'ar' | 'en' = 'fr'): void {
    const textareaMap = {
      fr: this.descriptionTextarea,
      ar: this.descriptionArabTextarea,
      en: this.descriptionEnglishTextarea
    };
    const textarea = textareaMap[target];
    if (!textarea) return;

    const fieldMap = {
      fr: 'description',
      ar: 'descriptionArab',
      en: 'descriptionEnglish'
    };
    const fieldName = fieldMap[target];
    const currentText = (this.piece as any)[fieldName] || '';

    const nativeElement = textarea.nativeElement;
    const start = nativeElement.selectionStart;
    const end = nativeElement.selectionEnd;
    const selectedText = currentText.substring(start, end);

    if (!selectedText) {
      this.showWarning('Veuillez sélectionner du texte à formater');
      return;
    }

    const formattedText = this.applyFormatting(command, selectedText);
    const newText = currentText.substring(0, start) + formattedText + currentText.substring(end);
    (this.piece as any)[fieldName] = newText;

    this.focusTextarea(nativeElement, start + formattedText.length);
    this.cdr.detectChanges();
  }

  private applyFormatting(command: string, text: string): string {
    const cmd = this.FORMATTING_COMMANDS[command];
    if (!cmd) return text;
    if (command === 'list') {
      return `<ul><li>${text}</li></ul>`;
    }
    return `<${cmd.tag}>${text}</${cmd.tag}>`;
  }

  private focusTextarea(textarea: HTMLTextAreaElement, position: number): void {
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(position, position);
    }, 0);
  }

  // ============= HELPERS =============
  private validateImageFile(file: File): boolean {
    const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif', 'image/webp'];
    const maxSize = 5 * 1024 * 1024;
    if (!validTypes.includes(file.type)) {
      Swal.fire({ icon: 'warning', title: 'Format non supporté', text: 'JPEG, PNG, JPG, GIF, WEBP uniquement', timer: 2000 });
      return false;
    }
    if (file.size > maxSize) {
      Swal.fire({ icon: 'warning', title: 'Fichier trop volumineux', text: 'Max 5MB', timer: 2000 });
      return false;
    }
    return true;
  }

  private fileToDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e: any) => resolve(e.target.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  // ============================================================
  // ==================== GALERIE ===============================
  // ============================================================

  setGalleryUploadMode(mode: GalleryUploadMode): void {
    if (this.isUploadingImages) return;
    if (this.isAwaitingGalleryValidation) this.cancelGalleryPreview(true);
    this.galleryUploadMode = mode;
  }

  openImagesSelector(): void {
    if (this.isAwaitingGalleryValidation) return;
    if (this.pieceImagesInput?.nativeElement) {
      this.pieceImagesInput.nativeElement.click();
    }
  }

  async selectFileimg(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    const validFiles: File[] = [];
    for (let i = 0; i < input.files.length; i++) {
      if (this.validateImageFile(input.files[i])) validFiles.push(input.files[i]);
    }

    if (validFiles.length === 0) { input.value = ''; return; }

    if (this.galleryUploadMode === 'direct') {
      await this.uploadMultipleImages(validFiles);
    } else {
      // Preview + validation
      this.galleryPreviewOriginalUrls = [];
      for (const file of validFiles) {
        const dataUrl = await this.fileToDataUrl(file);
        this.galleryPreviewOriginalUrls.push(dataUrl);
      }
      this.uploadViaImagesousproduit(validFiles);
    }

    input.value = '';
  }

  // ---------- Direct (original) ----------
  private async uploadMultipleImages(files: File[]): Promise<void> {
    this.isUploadingImages = true;
    try {
      for (const file of files) {
        await this.uploadSingleImage(file);
      }
    } finally {
      this.isUploadingImages = false;
    }
  }

  private async uploadSingleImage(file: File): Promise<void> {
    const toast = Swal.mixin({
      toast: true,
      position: 'top-end',
      showConfirmButton: false,
      timer: 3000,
      timerProgressBar: true
    });

    toast.fire({ icon: 'info', title: `Téléchargement de ${file.name}...` });

    try {
      const folder = 'pieces';
      const response = await this.awsService.uploadFile(file, folder).toPromise();
      if (response.success && response.urls && response.urls.length > 0) {
        this.uploadedImages.push(response.urls[0]);
        toast.fire({ icon: 'success', title: `${file.name} téléchargé` });
      } else {
        throw new Error(response.error || 'Upload failed');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.fire({ icon: 'error', title: `Échec de ${file.name}` });
    }
  }

  // ---------- Imagesousproduit (nouveau) ----------
  private uploadViaImagesousproduit(files: File[]): void {
    this.isUploadingImages = true;
    this.isAwaitingGalleryValidation = false;
    this.galleryPreviewProcessedUrls = [];

    this.awsService.imagesousproduit(files, 'pieces').subscribe({
      next: (response: ImageUploadResponse) => {
        this.isUploadingImages = false;
        if (response.success && response.urls && response.urls.length > 0) {
          this.galleryPreviewProcessedUrls = response.urls;
          this.isAwaitingGalleryValidation = true;
        } else {
          Swal.fire({
            icon: 'error',
            title: 'Erreur',
            text: (response.errors && response.errors[0]) || 'Échec du traitement',
            confirmButtonColor: '#f44336'
          });
          this.galleryPreviewOriginalUrls = [];
        }
        this.cdr.detectChanges();
      },
      error: () => {
        this.isUploadingImages = false;
        Swal.fire({ icon: 'error', title: 'Erreur', text: 'Impossible de traiter les images', confirmButtonColor: '#f44336' });
        this.galleryPreviewOriginalUrls = [];
        this.cdr.detectChanges();
      }
    });
  }

  validateGalleryPreview(): void {
    if (this.galleryPreviewProcessedUrls.length === 0) return;
    this.uploadedImages.push(...this.galleryPreviewProcessedUrls);

    this.galleryPreviewOriginalUrls = [];
    this.galleryPreviewProcessedUrls = [];
    this.isAwaitingGalleryValidation = false;
    Swal.fire({ icon: 'success', title: 'Images validées', timer: 1500, showConfirmButton: false });
    this.cdr.detectChanges();
  }

  cancelGalleryPreview(silent: boolean = false): void {
    const urls = [...this.galleryPreviewProcessedUrls];
    const doCancel = () => {
      urls.forEach((u: string) => this.awsService.deleteFile(u).subscribe({ error: () => {} }));
      this.galleryPreviewOriginalUrls = [];
      this.galleryPreviewProcessedUrls = [];
      this.isAwaitingGalleryValidation = false;
      this.cdr.detectChanges();
    };
    if (silent) { doCancel(); return; }
    Swal.fire({
      title: 'Refuser ces images ?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f44336',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Oui, refuser',
      cancelButtonText: 'Annuler'
    }).then(r => { if (r.isConfirmed) doCancel(); });
  }

  // ---------- Manipulation images ----------
  async removeImage(index: number): Promise<void> {
    const imageUrl = this.uploadedImages[index];
    const result = await Swal.fire({
      title: 'Êtes-vous sûr?',
      text: 'Voulez-vous vraiment supprimer cette image?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f44336',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Oui, supprimer!',
      cancelButtonText: 'Annuler'
    });

    if (result.isConfirmed) {
      try {
        await this.awsService.deleteFile(imageUrl).toPromise();
      } catch (error) {
        console.warn('Error deleting from S3:', error);
      }
      this.uploadedImages.splice(index, 1);
      this.showSuccess('Image supprimée', 'L\'image a été supprimée avec succès');
    }
  }

  moveImageUp(index: number): void {
    if (index > 0) {
      [this.uploadedImages[index], this.uploadedImages[index - 1]] =
        [this.uploadedImages[index - 1], this.uploadedImages[index]];
    }
  }

  moveImageDown(index: number): void {
    if (index < this.uploadedImages.length - 1) {
      [this.uploadedImages[index], this.uploadedImages[index + 1]] =
        [this.uploadedImages[index + 1], this.uploadedImages[index]];
    }
  }

  // ============= FORM SUBMISSION =============

  async updatePiece(): Promise<void> {
    if (this.isAwaitingGalleryValidation) {
      this.showWarning('Validation requise', 'Veuillez valider ou refuser les images de la galerie.');
      return;
    }
    if (this.isLoading) {
      this.showWarning('Veuillez patienter', 'Chargement en cours');
      return;
    }
    if (this.isUploadingImages) {
      this.showWarning('Veuillez patienter', 'Des images sont en cours de téléchargement');
      return;
    }

    this.isLoading = true;
    this.showLoadingDialog();

    const pieceId = this.activatedRoute.snapshot.paramMap.get('id');
    const updatedPiece = this.buildPiecePayload();

    this.piecesService.updatePiece(pieceId, updatedPiece)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => this.handleUpdateSuccess(),
        error: (err) => this.handleUpdateError(err)
      });
  }

  private buildPiecePayload(): any {
    return {
      name: this.piece.name || '',
      nameArab: this.piece.nameArab || '',
      nameEnglish: this.piece.nameEnglish || '',
      status: this.piece.status || 'DISPONIBLE',
      projetName: this.piece.projetName || '',
      category: this.piece.category || '',
      type: this.piece.type || '',
      description: this.piece.description || '',
      descriptionArab: this.piece.descriptionArab || '',
      descriptionEnglish: this.piece.descriptionEnglish || '',
      prixMdt: this.piece.prixMdt || '0',
      surface: this.piece.surface?.trim() ? this.piece.surface : null,
      photos: this.uploadedImages,
      videoUrl: this.piece.videoUrl?.trim() ? this.piece.videoUrl : null
    };
  }

  private handleUpdateSuccess(): void {
    Swal.close();
    this.isLoading = false;
    Swal.fire({
      icon: 'success',
      title: 'Succès',
      text: 'Appartement modifié avec succès!',
      timer: 2000,
      showConfirmButton: false
    }).then(() => {
      this.goBack();
    });
  }

  private handleUpdateError(error: any): void {
    Swal.close();
    this.isLoading = false;
    console.error('Error updating piece:', error);
    let errorMessage = 'Échec de la modification de l\'appartement';
    if (error.error?.message) errorMessage = error.error.message;
    else if (error.message) errorMessage = error.message;
    this.showError('Erreur', errorMessage);
  }

  // ============= NAVIGATION =============

  annule(): void {
    this.goBack();
  }

  private goBack(): void {
    this.location.back();
  }

  // ============= NOTIFICATIONS =============

  private showLoadingDialog(): void {
    Swal.fire({
      title: 'Mise à jour en cours...',
      html: 'Modification de votre appartement',
      allowOutsideClick: false,
      showConfirmButton: false,
      willOpen: () => Swal.showLoading()
    });
  }

  private showSuccess(title: string, text?: string): void {
    Swal.fire({ icon: 'success', title, text, timer: 2000, showConfirmButton: false });
  }

  private showWarning(title: string, text?: string): void {
    Swal.fire({ icon: 'warning', title, text, confirmButtonColor: '#3f51b5' });
  }

  private showError(title: string, text?: string): void {
    Swal.fire({ icon: 'error', title, text, confirmButtonColor: '#3f51b5' });
  }

  private handleError(message: string, error: any): void {
    console.error(message, error);
    this.showError(message);
  }

  getCategories(): string[] {
    const project = this.agenciesProjet.find(p => p.name === this.piece.projetName);
    return project?.categoryList || [];
  }
}